/*
 * Biodiscan — Ofertas en múltiplos (6+1, 4+1, 3+1, 5+1, 8+2…).
 *
 * Los descuentos de oferta los aplica la app SMART Discounts como un % por línea a partir de una
 * cantidad (p. ej. 7 uds → 14,28 %). Ese % solo equivale exactamente a "X + 1 gratis" cuando la
 * cantidad es un múltiplo del bloque (7, 14, 21…). Este archivo hace que las cantidades de los
 * productos en oferta solo puedan ser:
 *   - libres por debajo del primer bloque (1–6 en un 6+1), o
 *   - múltiplos del bloque (7, 14, 21…).
 * Con dos ofertas sobre el mismo producto (Feralive 5+1 y 8+2 → bloques 6 y 10): 1–5, 6, 10, 20, 30…
 *
 * Qué bloque tiene cada producto se decide en snippets/bd-offer-blocks.liquid (único sitio que hay
 * que tocar si cambian las ofertas). Aquí solo está la lógica común y la "red de seguridad" del
 * carrito:
 *   - window.BDOffer.next / prev / snapUp / isAllowed: los usan los selectores de cantidad
 *     (ficha, cajón y página del carrito, tarjetas y pedido rápido).
 *   - Guardia del carrito: tras cualquier cambio en el carrito comprueba cada formato en oferta y,
 *     si la cantidad total no es válida (p. ej. 7 en el carrito + 1 añadida desde una tarjeta = 8),
 *     la sube al siguiente múltiplo (14) —o la baja si no hay stock— y avisa al cliente.
 *     No actúa en la página de Pedido rápido, que ya lo gestiona ella misma.
 */
(function () {
  if (window.BDOffer) return;

  /** "6,10" → [6, 10] (ordenado, sin valores no válidos). */
  function parse(blocks) {
    if (Array.isArray(blocks)) return blocks.filter((n) => n > 1).sort((a, b) => a - b);
    return String(blocks || '')
      .split(',')
      .map((s) => parseInt(s, 10))
      .filter((n) => n > 1)
      .sort((a, b) => a - b);
  }

  /** ¿Es válida esta cantidad para un producto con estos bloques? */
  function isAllowed(qty, blocks) {
    const B = parse(blocks);
    if (!B.length || qty <= 0) return true;
    if (qty < B[0]) return true;
    const big = B[B.length - 1];
    if (qty % big === 0) return true;
    // Múltiplos de los bloques pequeños, solo por debajo del bloque grande (Feralive: 6 sí, 12 no).
    return B.some((b) => b !== big && qty < big && qty % b === 0);
  }

  /** Siguiente cantidad válida por encima de qty. */
  function next(qty, blocks) {
    const B = parse(blocks);
    if (!B.length) return qty + 1;
    let q = Math.max(0, qty) + 1;
    while (!isAllowed(q, B)) q++;
    return q;
  }

  /** Cantidad válida anterior por debajo de qty (0 si no hay ninguna). */
  function prev(qty, blocks) {
    const B = parse(blocks);
    if (!B.length) return Math.max(0, qty - 1);
    let q = qty - 1;
    while (q > 0 && !isAllowed(q, B)) q--;
    return Math.max(0, q);
  }

  /** qty si es válida; si no, la siguiente válida (se redondea hacia arriba). */
  function snapUp(qty, blocks) {
    return isAllowed(qty, blocks) ? qty : next(qty, blocks);
  }

  /** Texto para el cliente: "7, 14, 21…" o "6, 10, 20…". */
  function describe(blocks) {
    const B = parse(blocks);
    if (!B.length) return '';
    const out = [];
    let q = B[0];
    while (out.length < 3) {
      out.push(q);
      q = next(q, B);
    }
    return `${out.join(', ')}…`;
  }

  /* ---------- Aviso flotante ---------- */
  let toastEl;
  let toastTimer;
  function toast(msg) {
    if (!msg) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'bd-offer-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      const style = document.createElement('style');
      style.textContent =
        '.bd-offer-toast{position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:2147483000;' +
        'max-width:min(92vw,520px);padding:12px 16px;border-radius:8px;background:#0b5813;color:#fff;' +
        'font:inherit;font-size:14px;line-height:1.35;box-shadow:0 6px 24px rgba(0,0,0,.2);opacity:0;' +
        'pointer-events:none;transition:opacity .2s}.bd-offer-toast.is-visible{opacity:1;pointer-events:auto}';
      document.head.appendChild(style);
      document.body.appendChild(toastEl);
      toastEl.addEventListener('click', () => toastEl.classList.remove('is-visible'));
    }
    toastEl.textContent = msg;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 7000);
  }

  window.BDOffer = { parse, isAllowed, next, prev, snapUp, describe, toast };

  /* ---------- Guardia del carrito ---------- */
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let running = false;
  let again = false;
  let timer;
  let ignoreUntil = 0;

  async function getRules() {
    const res = await fetch(`${root}?sections=bd-offer-rules`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!res.ok) return {};
    const data = await res.json();
    const html = data['bd-offer-rules'] || '';
    const m = html.match(/<script[^>]*data-bd-offer-rules[^>]*>([\s\S]*?)<\/script>/);
    return m ? JSON.parse(m[1]) : {};
  }

  async function getCart() {
    const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!res.ok) throw new Error(res.status);
    return res.json();
  }

  async function notifyTheme(cart) {
    try {
      const { CartLinesUpdateEvent } = await import('@shopify/events');
      const deferred = CartLinesUpdateEvent.createPromise();
      document.body.dispatchEvent(
        new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'bd-offer-guard', itemCount: cart.item_count, didError: false },
      });
    } catch (e) {
      // Si el tema no tiene estos eventos, el carrito ya está corregido; solo no se refresca el cajón.
    }
  }

  async function check() {
    if (running) {
      again = true;
      return;
    }
    // El Pedido rápido corrige sus propias cantidades.
    if (document.querySelector('.qo')) return;
    running = true;
    try {
      const cart = await getCart();
      if (!cart.item_count) return;
      const rules = await getRules();
      // Unidades por formato y la última línea "normal" (sin _oferta) de cada uno.
      const byVariant = new Map();
      cart.items.forEach((it, i) => {
        const rule = rules[it.variant_id];
        if (!rule || !rule.b) return;
        const g = byVariant.get(it.variant_id) || { total: 0, lines: [], rule, title: it.product_title };
        g.total += it.quantity;
        g.lines.push(i);
        byVariant.set(it.variant_id, g);
      });
      const quantities = cart.items.map((it) => it.quantity);
      const msgs = [];
      byVariant.forEach((g) => {
        if (isAllowed(g.total, g.rule.b)) return;
        let target = snapUp(g.total, g.rule.b);
        const stock = g.rule.s;
        if (stock != null && target > stock) target = prev(stock + 1, g.rule.b);
        const delta = target - g.total;
        if (!delta) return;
        // Se ajusta la última línea normal (o la última que haya).
        const plain = g.lines.filter((i) => !(cart.items[i].properties && cart.items[i].properties._oferta));
        const idx = (plain.length ? plain : g.lines)[(plain.length ? plain : g.lines).length - 1];
        quantities[idx] = Math.max(0, quantities[idx] + delta);
        msgs.push(
          `${g.title}: ${g.total} → ${target} uds. Las ofertas van en ${describe(g.rule.b)} para que el descuento cuadre.`
        );
      });
      if (!msgs.length) return;
      ignoreUntil = Date.now() + 4000;
      const res = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: quantities }),
      });
      if (!res.ok) return;
      const fresh = await getCart();
      await notifyTheme(fresh);
      toast(`Hemos ajustado tu pedido. ${msgs.join(' ')}`);
    } catch (e) {
      // Sin conexión o error puntual: se vuelve a comprobar en el siguiente cambio del carrito.
    } finally {
      running = false;
      if (again) {
        again = false;
        schedule(800);
      }
    }
  }

  function schedule(delay) {
    clearTimeout(timer);
    timer = setTimeout(check, delay);
  }

  import('@shopify/events')
    .then(({ CartLinesUpdateEvent }) => {
      document.addEventListener(CartLinesUpdateEvent.eventName, () => {
        if (Date.now() < ignoreUntil) return;
        // Se deja tiempo a que el tema termine de guardar y repintar el carrito.
        schedule(1500);
      });
    })
    .catch(() => {});

  // Al cargar la página también se revisa (por si el carrito viene de otro dispositivo o de antes).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => schedule(1200));
  } else {
    schedule(1200);
  }
})();
